import {
  humanizePermissionCode,
  resolveCatalogEntry,
  type PermissionCatalogEntry,
} from './permissionGrouping'
import {
  resolveAuthoritativePermissionPlacement,
  productionModuleOrderIndex,
} from './productionPermissionGrouping'
import {normalizeBackendLabel} from '../../../../_metronic/layout/components/sidebar/sidebar-menu/backendMenuRouteMap'
import {
  isPermanentlyHiddenPermissionCode,
  isPermanentlyHiddenPlacement,
} from '../../../../app/shared/navigation/permanentlyHiddenMenus'
import {
  isProcurementMenuLabel,
  procurementNavigationRowKey,
  resolveProcurementMatrixAction,
  stdPreferenceRank,
} from './procurementPermissionMatrix'

export type StdAction = 'view' | 'add' | 'edit' | 'delete' | 'approve' | 'download'

export type MatrixRow = {
  rowKey: string
  moduleName: string
  menuName: string
  subMenuName: string
  standard: Partial<Record<StdAction, string>>
  standardHints: Partial<Record<StdAction, string>>
  extraChecks: Array<{code: string; label: string; catalogDetail?: string}>
}

export const STD_ORDER: StdAction[] = ['view', 'add', 'edit', 'delete', 'approve', 'download']

export const STD_LABELS: Record<StdAction, string> = {
  view: 'View',
  add: 'Add',
  edit: 'Edit',
  delete: 'Delete',
  approve: 'Approve',
  download: 'Download',
}

const SUFFIX_STD: Record<string, StdAction> = {
  VIEW: 'view',
  READ: 'view',
  SHOW: 'view',
  LIST: 'view',
  CREATE: 'add',
  ADD: 'add',
  INSERT: 'add',
  NEW: 'add',
  UPDATE: 'edit',
  EDIT: 'edit',
  MODIFY: 'edit',
  DELETE: 'delete',
  REMOVE: 'delete',
  APPROVE: 'approve',
  APPROVAL: 'approve',
  EXPORT: 'download',
  DOWNLOAD: 'download',
}

const SUFFIX_EXT_LABEL: Record<string, string> = {
  IMPORT: 'Import',
  MANAGE: 'Manage',
  ASSIGN: 'Assign',
  SEND: 'Send',
  AWARD: 'Award',
  CLOSE: 'Close',
  SUBMIT: 'Submit',
  COMPARE: 'Compare',
  SELECT: 'Select',
  REWORK: 'Rework',
  ADD_ITEMS: 'Add Items',
}

const PREFIX_STD: Record<string, StdAction> = {
  READ: 'view',
  VIEW: 'view',
  SHOW: 'view',
  LIST: 'view',
  CREATE: 'add',
  ADD: 'add',
  INSERT: 'add',
  UPDATE: 'edit',
  EDIT: 'edit',
  MODIFY: 'edit',
  DELETE: 'delete',
  REMOVE: 'delete',
  APPROVE: 'approve',
  EXPORT: 'download',
  DOWNLOAD: 'download',
}

const PREFIX_EXT_LABEL: Record<string, string> = {
  IMPORT: 'Import',
  MANAGE: 'Manage',
  ASSIGN: 'Assign',
  SEND: 'Send',
  AWARD: 'Award',
  CLOSE: 'Close',
  SUBMIT: 'Submit',
}

export const normalizeCode = (value: string): string => value.trim().toUpperCase()

function shortActionLabel(text: string): string {
  return (text || '').replace(/^\s*can\s+/i, '').trim() || text
}

type ParsedToken = {
  base: string
  std: StdAction | null
  extLabel: string | null
}

function actionHintFromDisplayName(displayName: string): StdAction | null {
  if (/\bDELETE\b|\bREMOVE\b/i.test(displayName)) return 'delete'
  if (/\b(APPROVE|APPROVAL)\b/i.test(displayName)) return 'approve'
  if (/\bDOWNLOAD\b/i.test(displayName)) return 'download'
  if (/\bEXPORT\b/i.test(displayName)) return null
  if (/\bIMPORT\b/i.test(displayName)) return null
  if (/\b(UPDATE|EDIT|MODIFY)\b/i.test(displayName)) return 'edit'
  if (/\b(CREATE|ADD|INSERT)\b/i.test(displayName)) return 'add'
  if (/\b(VIEW|READ|LIST)\b/i.test(displayName)) return 'view'
  const u = displayName.toUpperCase()
  if (u.includes('CAN VIEW') || u.includes('CAN READ')) return 'view'
  if (u.includes('CAN ADD') || u.includes('CAN CREATE')) return 'add'
  if (u.includes('CAN EDIT') || u.includes('CAN UPDATE')) return 'edit'
  if (u.includes('CAN DELETE')) return 'delete'
  if (u.includes('CAN APPROVE')) return 'approve'
  if (u.includes('CAN DOWNLOAD')) return 'download'
  return null
}

function parsePermissionTokens(permissionCode: string): ParsedToken | null {
  const u = normalizeCode(permissionCode)
  const parts = u.split('_').filter(Boolean)
  if (parts.length === 0) return null

  const last = parts[parts.length - 1]
  const first = parts[0]

  const sx = SUFFIX_STD[last]
  if (sx && parts.length >= 2) {
    return {base: parts.slice(0, -1).join('_'), std: sx, extLabel: null}
  }
  const se = SUFFIX_EXT_LABEL[last]
  if (se && parts.length >= 2) {
    return {base: parts.slice(0, -1).join('_'), std: null, extLabel: se}
  }

  const px = PREFIX_STD[first]
  if (px && parts.length >= 2) {
    return {base: parts.slice(1).join('_'), std: px, extLabel: null}
  }
  const pe = PREFIX_EXT_LABEL[first]
  if (pe && parts.length >= 2) {
    return {base: parts.slice(1).join('_'), std: null, extLabel: pe}
  }

  return null
}

function sortMatrixRows(rows: MatrixRow[]): MatrixRow[] {
  return [...rows].sort((a, b) => {
    const ma = productionModuleOrderIndex(a.moduleName)
    const mb = productionModuleOrderIndex(b.moduleName)
    if (ma !== mb) return ma - mb
    const c1 = a.menuName.localeCompare(b.menuName)
    if (c1 !== 0) return c1
    const c2 = a.subMenuName.localeCompare(b.subMenuName)
    if (c2 !== 0) return c2
    return a.rowKey.localeCompare(b.rowKey)
  })
}

function pushExtra(row: MatrixRow, code: string, label: string, catalogDetail?: string) {
  if (!row.extraChecks.some((e) => normalizeCode(e.code) === normalizeCode(code))) {
    const d = catalogDetail?.trim()
    row.extraChecks.push({code, label, ...(d ? {catalogDetail: d.slice(0, 240)} : {})})
  }
}

function mergeStandard(row: MatrixRow, act: StdAction, code: string, displayName: string) {
  const existing = row.standard[act]
  const catalogHint = shortActionLabel(displayName?.trim() || '').trim().slice(0, 200)
  if (existing && normalizeCode(existing) !== normalizeCode(code)) {
    const keepExisting =
      stdPreferenceRank(act, existing) <= stdPreferenceRank(act, code)
    if (keepExisting) {
      pushExtra(row, code, `${STD_LABELS[act]} (alternate)`, catalogHint || undefined)
      return
    }
    pushExtra(row, existing, `${STD_LABELS[act]} (alternate)`, row.standardHints[act])
  }
  row.standard[act] = code
  if (catalogHint.length > 0) row.standardHints[act] = catalogHint
}

function resolveMatrixRowKey(
  moduleName: string,
  menuName: string,
  subMenuName: string,
  resourceFamily: string
): string {
  if (isProcurementMenuLabel(menuName)) {
    return procurementNavigationRowKey(moduleName, menuName, subMenuName)
  }
  return `${moduleName}::${menuName}::${subMenuName}::${resourceFamily}`
}

export function resolveMatrixResourceFamily(
  permissionCode: string,
  placement: ReturnType<typeof resolveAuthoritativePermissionPlacement>
): string {
  const u = normalizeCode(permissionCode)
  if (u === 'EDIT_USER_PASSWORD') {
    return 'USER'
  }
  const menu = normalizeBackendLabel(placement.menuLabel)
  const sub = normalizeBackendLabel(placement.subMenuLabel)

  if (menu === 'pms' && (sub === 'dashboard' || sub === '' || sub === '—')) {
    if (/^EXPORT_PMS$|^READ_PMS_DASHBOARD$|^PMS_DASHBOARD|^PMS_DASH/i.test(u)) return 'PMS_DASHBOARD'
  }
  if (menu === 'pms' && sub === 'jobs') {
    if (/PMS_JOB|^COMPLETE_PMS|^DOWNLOAD_PMS$/i.test(u)) return 'PMS_JOB'
  }

  const parsed = parsePermissionTokens(permissionCode)
  if (parsed) return parsed.base
  return u
}

export function buildPermissionMatrixRows(catalog: PermissionCatalogEntry[]): MatrixRow[] {
  const map = new Map<string, MatrixRow>()

  for (const raw of catalog) {
    const e = resolveCatalogEntry(raw)
    const code = e.permissionCode
    if (isPermanentlyHiddenPermissionCode(code)) {
      continue
    }
    const placement = resolveAuthoritativePermissionPlacement(raw, catalog)
    if (isPermanentlyHiddenPlacement(placement.moduleLabel, placement.menuLabel, placement.subMenuLabel)) {
      continue
    }
    const moduleName = placement.moduleLabel
    const menuName = placement.menuLabel
    const subMenuName = placement.subMenuLabel

    const parsed = parsePermissionTokens(code)
    let std: StdAction | null = null
    let extLabel: string | null = null

    if (parsed) {
      std = parsed.std
      extLabel = parsed.extLabel
    } else {
      const hint = actionHintFromDisplayName(e.displayName || '')
      if (hint) std = hint
    }

    const resourceFamily = resolveMatrixResourceFamily(code, placement)
    const rowKey = resolveMatrixRowKey(moduleName, menuName, subMenuName, resourceFamily)
    let row = map.get(rowKey)
    if (!row) {
      row = {
        rowKey,
        moduleName,
        menuName,
        subMenuName,
        standard: {},
        standardHints: {},
        extraChecks: [],
      }
      map.set(rowKey, row)
    }

    const procurementAction =
      isProcurementMenuLabel(menuName) ? resolveProcurementMatrixAction(code) : null
    if (procurementAction?.kind === 'hidden-alias') {
      continue
    }
    if (procurementAction?.kind === 'standard') {
      mergeStandard(row, procurementAction.action, code, e.displayName || '')
      continue
    }
    if (procurementAction?.kind === 'extra') {
      const detail =
        shortActionLabel(e.displayName?.trim() || '').trim().slice(0, 240) ||
        (e.description || '').trim().slice(0, 240)
      pushExtra(
        row,
        code,
        procurementAction.label,
        detail.length > 0 && detail !== procurementAction.label ? detail : undefined
      )
      continue
    }

    if (['MANAGE_CREW_CLAIM_DRAFT', 'MANAGE_CREW_DEDUCTION_DRAFT'].includes(normalizeCode(code))) {
      const label = normalizeCode(code) === 'MANAGE_CREW_CLAIM_DRAFT' ? 'claims' : 'deductions'
      pushExtra(row, code, `Edit and submit ${label} on behalf of crew`,
        'Drafts for onboard and signed-off crew. Creating requires Create; editing requires Edit. Review permissions remain separate.')
      continue
    }

    if (normalizeCode(code) === 'MANAGE_CREW_ALLOTMENT_DRAFT') {
      pushExtra(row, code, 'Edit and submit allotment on behalf of crew',
        'Drafts for onboard and signed-off crew. Editing also requires Edit permission; review permissions remain separate.')
      continue
    }

    if (normalizeCode(code) === 'EDIT_USER_PASSWORD') {
      const detail =
        shortActionLabel(e.displayName?.trim() || 'Edit Password').trim().slice(0, 240) ||
        (e.description || '').trim().slice(0, 240)
      pushExtra(row, code, 'Edit Password', detail.length > 0 ? detail : undefined)
      continue
    }

    if (normalizeCode(code) === 'OFFICE_REVIEW_QHSE_DEFECT') {
      pushExtra(row, code, 'Office Review Defect', 'Save office remarks after satisfactory or TBR closure. Does not approve or reopen the defect.')
      continue
    }

    if (extLabel) {
      const detail =
        shortActionLabel(e.displayName?.trim() || '').trim().slice(0, 240) ||
        (e.description || '').trim().slice(0, 240)
      pushExtra(row, code, extLabel, detail.length > 0 && detail !== extLabel ? detail : undefined)
    } else if (std) {
      mergeStandard(row, std, code, e.displayName || '')
    } else {
      const dn = shortActionLabel(e.displayName?.trim() || humanizePermissionCode(code))
      const desc = (e.description || '').trim().slice(0, 240)
      pushExtra(
        row,
        code,
        dn,
        desc.length > 0 && desc.toUpperCase() !== dn.toUpperCase() ? desc : undefined
      )
    }
  }

  return sortMatrixRows(Array.from(map.values()))
}

export function countPmsUiGroups(rows: MatrixRow[]): {dashboard: number; jobs: number} {
  const dashboard = rows.filter(
    (r) => r.menuName.toLowerCase() === 'pms' && r.subMenuName.toLowerCase() === 'dashboard'
  ).length
  const jobs = rows.filter(
    (r) => r.menuName.toLowerCase() === 'pms' && r.subMenuName.toLowerCase() === 'jobs'
  ).length
  return {dashboard, jobs}
}
